import { useState } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import GridTable from '../../common/GridTable/GridTable'
import Input from '../../common/Input/index'
import Select from '../../common/Select/index'
import Button from '../../common/Button/index'
import Typography from '../../common/Typography/index'
import LoadingIndicator from '../../state/LoadingIndicator'
import { MdOutlineArrowDropDown } from 'react-icons/md'
import { createFeedback } from '../../../features/feedback/service/feedbackApi'

const columns = [
  { colname: 'Generate ticket', colStyle: 'flex justify-start' },
  { colname: 'x', colStyle: 'flex justify-end' },
]

const GenerateTicketForm = ({
  onSubmit,
  selectedTicketStatus,
  setSelectedTicketStatus,
  selectedPriority,
  setSelectedPriority,
  selectedCategory,
  setSelectedCategory,
  selectedSource,
  setSelectedSource,
  selectedFeedback,
}) => {
  const [ticketName, setTicketName] = useState('')
  const [ticketOwner, setTicketOwner] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  
  const dispatch = useDispatch()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      // Prepare ticket data
      const ticketData = {
        subject: ticketName || `Ticket from feedback: ${selectedFeedback?.subject || 'No Subject'}`,
        message: selectedFeedback ? 
          `This ticket was generated from feedback:\n\nOriginal Message: ${selectedFeedback.message || selectedFeedback.description || 'No message content'}\n\nFrom: ${selectedFeedback.email || selectedFeedback.user?.email || 'Unknown'}` :
          `New ticket: ${ticketName}`,
        status: selectedTicketStatus,
        priority: selectedPriority,
        category: selectedCategory,
        source: selectedSource,
        ticketOwner: ticketOwner,
        // Include original feedback reference if available
        originalFeedbackId: selectedFeedback?._id,
        // Use feedback user info if available
        email: selectedFeedback?.email || selectedFeedback?.user?.email,
        userId: selectedFeedback?.user?._id || selectedFeedback?.userId,
        type: 'ticket', // Distinguish from regular feedback
      }

      // Create the ticket using the feedback API
      const response = await createFeedback(ticketData)
      
      console.log('Ticket created successfully:', response.data)
      
      // Reset form
      setTicketName('')
      setTicketOwner('')
      setSelectedTicketStatus('new')
      setSelectedPriority('medium')
      setSelectedCategory('bug')
      setSelectedSource('inApp')
      
      // Call onSubmit callback to close modal and refresh data
      if (onSubmit) {
        onSubmit(response.data)
      }
      
    } catch (error) {
      console.error('Error creating ticket:', error)
      setError(error.response?.data?.message || error.message || 'Failed to create ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-md mx-auto">
      <form onSubmit={handleSubmit}>
        <GridTable columns={columns} gridCols="grid-cols-2">
          {/* Display error message */}
          {error && (
            <div className="col-span-full mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <Typography className="text-red-700 text-sm">{error}</Typography>
            </div>
          )}

          {/* Show reference to original feedback if available */}
          {selectedFeedback && (
            <div className="col-span-full mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <Typography className="text-blue-700 text-sm font-medium">
                Creating ticket from feedback:
              </Typography>
              <Typography className="text-blue-600 text-sm mt-1">
                {selectedFeedback.subject || 'No Subject'} - From: {selectedFeedback.email || selectedFeedback.user?.email || 'Unknown'}
              </Typography>
            </div>
          )}

          <div className="col-span-full">
            <label htmlFor="ticketName" className="block text-sm font-medium text-gray-700 mb-1">
              Ticket Name *
            </label>
            <Input
              id="ticketName"
              name="ticketName"
              size="small"
              value={ticketName}
              onChange={(e) => setTicketName(e.target.value)}
              placeholder={selectedFeedback ? 
                `Ticket from: ${selectedFeedback.subject || 'Feedback'}` : 
                "Create a ticket from this conversation"}
              className="border border-secondary w-full"
              required
            />
          </div>

          <div className="col-span-full">
            <label htmlFor="ticketStatus" className="block text-sm font-medium text-gray-700 mb-1">
              Ticket Status *
            </label>
            <Select
              id="ticketStatus"
              name="ticketStatus"
              value={selectedTicketStatus}
              onChange={e => setSelectedTicketStatus(e.target.value)}
              icon={<MdOutlineArrowDropDown />}
              size="small"
              options={[
                { value: 'new', label: 'New' },
                { value: 'open', label: 'Open' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'postponed', label: 'Postponed' },
              ]}
              required
            />
          </div>

          <div className="col-span-full">
            <label htmlFor="ticketPriority" className="block text-sm font-medium text-gray-700 mb-1">
              Priority *
            </label>
            <Select
              id="ticketPriority"
              name="ticketPriority"
              value={selectedPriority}
              onChange={e => setSelectedPriority(e.target.value)}
              icon={<MdOutlineArrowDropDown />}
              size="small"
              options={[
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' },
              ]}
              required
            />
          </div>

          <div className="col-span-full">
            <label htmlFor="ticketCategory" className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <Select
              id="ticketCategory"
              name="ticketCategory"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              icon={<MdOutlineArrowDropDown />}
              size="small"
              options={[
                { value: 'bug', label: 'Bug' },
                { value: 'complaint', label: 'Complaint' },
                { value: 'suggestion', label: 'Suggestion' },
                { value: 'others', label: 'Others' },
              ]}
              required
            />
          </div>

          <div className="col-span-full">
            <label htmlFor="ticketOwner" className="block text-sm font-medium text-gray-700 mb-1">
              Ticket Owner
            </label>
            <Input 
              id="ticketOwner" 
              name="ticketOwner"
              size="small" 
              value={ticketOwner}
              onChange={(e) => setTicketOwner(e.target.value)}
              placeholder="Assign ticket owner (optional)"
              className="border border-secondary w-full" 
            />
          </div>

          <div className="col-span-full">
            <label htmlFor="ticketSource" className="block text-sm font-medium text-gray-700 mb-1">
              Source *
            </label>
            <Select
              id="ticketSource"
              name="ticketSource"
              value={selectedSource}
              onChange={e => setSelectedSource(e.target.value)}
              icon={<MdOutlineArrowDropDown />}
              size="small"
              options={[
                { value: 'inApp', label: 'In-app' },
                { value: 'website', label: 'Website' },
                { value: 'email', label: 'Email' },
                { value: 'phone', label: 'Phone' },
              ]}
              required
            />
          </div>

          <div className="col-span-full flex items-center justify-center mt-4">
            <Button 
              type="submit"
              size="small" 
              disabled={isSubmitting || !ticketName.trim()}
              className="min-w-[100px]"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <LoadingIndicator size="small" />
                  <span>Creating...</span>
                </div>
              ) : (
                'Create'
              )}
            </Button>
          </div>
        </GridTable>
      </form>
    </div>
  )
}

GenerateTicketForm.propTypes = {
  onSubmit: PropTypes.func,
  selectedTicketStatus: PropTypes.string,
  setSelectedTicketStatus: PropTypes.func,
  selectedPriority: PropTypes.string,
  setSelectedPriority: PropTypes.func,
  selectedCategory: PropTypes.string,
  setSelectedCategory: PropTypes.func,
  selectedSource: PropTypes.string,
  setSelectedSource: PropTypes.func,
  selectedFeedback: PropTypes.object, // The feedback item to create ticket from
}

export default GenerateTicketForm